import React, { useState } from 'react';
import { Message as UIMessage } from '@shopee/chatbot-ui';
import Webform, { WebFormProps } from '../Webform';
import { ButtonGroupProps } from '@shopee/chatbot-ui/lib/components/ButtonGroup';
import { ButtonAction, getButtonAction, getButtonType } from '@/utils/getButtonData';
import { ChatbotUIBasicProps } from '@/types/common';
import reporter from '@/utils/report';
import { IntentSource, INPUT_TYPE, TRACKING_MSG_TYPE } from '@/utils/constants';
import { handleClickHistoryButton } from '@/utils/tools';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/tracking/constants';
import { updateDialogueInfo } from '@/utils/sessionUtils';
import { featureToggle, FEATURE_TOGGLE_TYPE } from '@/utils/featureToggleConfig';
import { inject, observer } from 'mobx-react';
import { getQueryForm } from '@/utils/instantLoaded/toolsWithoutDeps';
import { getChildInject } from '@/utils/instantLoaded/tools';

enum MsgType {
  default = 0,
  changeOrder,
}

export interface ButtonValues {
  button_id: string;
  button_style: number;
  button_text: string;
  action: {
    action_type: number;
    task_bot_action: any;
  };
}

export type MessageProps = {
  taskbotInfo: any;
  content: string;
  jumpNodeMap: Record<string, unknown> | null;
  msgType: MsgType;
  buttonGroup: Omit<ButtonGroupProps, 'buttons'> & {
    buttons: ButtonValues[];
  };
  className?: string;
} & ChatbotUIBasicProps;

const Message: React.FC<MessageProps> = (props) => {
  if (!props.content && !props?.buttonGroup?.buttons?.length) {
    return null;
  }
  const [webformProps, setWebformProps] = useState<WebFormProps | null>(null);

  const submitCb = (btn_id: string) => {
    const newButtons = props.buttonGroup.buttons.map((btn, i) => {
      if (btn.button_id === btn_id) {
        return {
          ...btn,
          disabled: true,
        };
      } else {
        return btn;
      }
    });
    const newProps = {
      buttonGroup: {
        ...props.buttonGroup,
        buttons: newButtons,
      },
    };
    updateDialogueInfo(newProps, props.listPos);
  };
  const onClickButtonCb = (props: WebFormProps) => {
    setWebformProps(props);
  };
  const buttonGroup = composeTFEButtonGroupProps(props, onClickButtonCb);

  const { jumpNodeMap, linkTrackingData } = composeTFEJumpNodeMapProps(props);
  const reportImageCount = (imageCount: number) => {
    imageCount > 0
      && reporter.apiBatchReport({ type: 'count', value: { label: 'get_message_image_success', count: imageCount }});
  };

  const reportImageError = (src: string) => {
    const { kbBotId, nodePointId } = props;

    reporter.apiBatchReport({ type: 'count', value: { label: 'get_message_image_failed' }});
    reporter.apiLog(`[Get Message Image Failed] , kb_bot_id: ${kbBotId}, node_point_id: ${nodePointId}, src: ${src}`, { level: 'warn' });
  };

  const onImageError = (e: any) => {
    if (e?.target?.tagName !== 'IMG') {
      return;
    }

    if (e.preventDefault) {
      e.preventDefault();
    } else if (window.event) {
      window.event.returnValue = false;
    }

    reportImageError(e?.target?.src);
  };

  const onImageLoad = (e: any) => {
    if (e?.target?.tagName !== 'IMG') {
      return;
    }

    reportImageCount(1);
  };

  return (
    <>
      <UIMessage
        className={props.className}
        content={props.content}
        jumpNodeMap={jumpNodeMap}
        jumpOrderShortCutMap={{
          nextParams: {
            input_type: INPUT_TYPE.INPUT_BY_CLICK_ORDER_SHORTCUT,
            source: IntentSource.Shortcuts,
            session_id: props.sessionId,
          },
        }}
        buttonGroup={buttonGroup as ButtonGroupProps}
        linkTrackingData={linkTrackingData}
        msgType={props.msgType}
        disableSpecialLink={props.isHistory}
        onImageError={onImageError}
        onImageLoad={onImageLoad}
        isUIOptimisationButton={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON)}
        isUIOptimisationTable={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_TABLE)}
      />
      <Webform
        {...(webformProps || {})}
        submitCb={submitCb}
        isHistory={props.isHistory}
        historySessionId={props.historySessionId}
        sessionId={props.sessionId}
        historyDialogueId={props.historyDialogueId}
        dialogueId={props.dialogueId}
        listPos={props.listPos}
        onClosePopup={() => setWebformProps({ ...webformProps, active: false } as any)}
      />
    </>
  );
};

export default inject(getChildInject('initStore'))(observer(Message));

export function composeTFEJumpNodeMapProps(
  props: { jumpNodeMap: Record<string, unknown> | null } & ChatbotUIBasicProps,
) {
  const jumpNodeMap = Object.fromEntries(
    Object.entries(props.jumpNodeMap || {}).map((item) => [
      item[0],
      {
        nextParams: {
          input_type: INPUT_TYPE.BUTTON,
          session_id: props.sessionId,
          dialogue_id: props.dialogueId,
          thread_node_id: props.threadNodeId,
          thread_id: props.threadId,
          thread_id_v2: props.threadIdV2,
          round_id: props.roundId,
          is_bypass: (item[1] as { is_bypass: boolean })?.is_bypass || false,
          message_extra: { task_bot_action: Object.assign({}, item[1], { button_id: item[0] }) },
        },
      },
    ]),
  );

  const linkTrackingData = {
    jumpIntent: {
      chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
      chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
    },
  };

  return { jumpNodeMap, linkTrackingData };
}

export function composeTFEButtonGroupProps(
  props: {
    buttonGroup: Omit<ButtonGroupProps, 'buttons'> & {
      buttons: ButtonValues[];
    };
  } & ChatbotUIBasicProps,
  buttonClickCb?: (props: WebFormProps) => void,
) {
  const { customCallback = {}} = props;
  const buttonGroup = {
    ...props.buttonGroup,
    buttons: props.buttonGroup.buttons.map((btn: any, index: number) => {
      const buttonProps = {
        hidden: btn.hidden,
        text: btn.button_text,
        type: getButtonType(btn.button_style),
        disabled: btn.disabled ? btn.disabled : props.isHistory ? true : false,
        onDisableClick: props.isHistory ? handleClickHistoryButton : undefined,
        listPos: props.listPos,
        isUIOptimisationButton: featureToggle._toggles[FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON](),
        action: {
          callback: buttonClickCb,
          ...btn.action,
          func: getButtonAction(btn?.action?.action_type),
          info: props,
          buttonInfo: {
            ...btn,
            index,
          },
          taskbotInfo: props.taskbotInfo,
          customCallback,
          listPos: props.listPos,
        },
        auto_action: undefined,
        multiple_action: {
          // callback: buttonClickCb,
          ...btn.multiple_action,
          func: getButtonAction(btn?.multiple_action?.action_type),
          info: props,
          buttonInfo: {
            ...btn,
            index,
          },
          taskbotInfo: props.taskbotInfo,
          customCallback,
        },
        trackingData: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.STANDARDIZE_BUTTON],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.STANDARDIZE_BUTTON],
          trackingdataBasic: {
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
          },
          trackingdataTarget: {
            button_text: btn.button_text,
            button_id: btn.button_id,
            button_type: btn.action?.action_type?.toString?.(),
          },
        },
      };
      switch (getButtonAction(btn?.action?.action_type)) {
        case 'next':
          buttonProps.action.nextParams = {
            message_text: btn.button_text,
            input_type: INPUT_TYPE.BUTTON,
            session_id: props.sessionId,
            dialogue_id: props.dialogueId,
            thread_node_id: props.threadNodeId,
            thread_id: props.threadId,
            thread_id_v2: props.threadIdV2,
            round_id: props.roundId,
            is_bypass: btn?.action?.is_bypass || false,
            message_extra: { task_bot_action: Object.assign({ task_bot_info: props.taskbotInfo }, btn.action.task_bot_action) },
          };
          break;
        case 'getIntent':
          const intent = btn?.action?.intent;
          buttonProps.action.nextParams = {
            session_id: props.sessionId,
            intent_id: intent?.intent_id,
            kb_bot_id: intent?.bot_id,
            source: IntentSource.KB,
            is_category: false,
            thread_node_id: props.threadNodeId,
            thread_id: props.threadId,
            thread_id_v2: props.threadIdV2,
            round_id: props.roundId,
            is_bypass: btn?.action?.is_bypass || false,
            dialogue_id: props.dialogueId,
          };
          break;
        // SPCB-28906 新增 webform
        case 'popupWebform':
          if (buttonProps.action.buttonInfo.callback_action) {
            buttonProps.action.buttonInfo.callback_action = getRedirectCallbackParams({
              action: buttonProps.action,
              btn,
              props,
            });
          }
          break;
        case 'redirection':
          // https://jira.shopee.io/browse/SPCB-32618
          // driver vn webform 跳转需要填充csFillToken
          const { searchUrl = '' } = props.initStore as any;
          const csFillToken = getQueryForm('csFillToken', searchUrl);
          if (csFillToken && btn?.action?.action_type === ButtonAction.JumpFormstack) {
            const originFormstack = buttonProps.action.formstack;
            buttonProps.action.formstack = `${originFormstack}${
              originFormstack.includes('?') ? '&' : '?'
            }csFillToken=${csFillToken}`;
          }

          if (buttonProps.action.buttonInfo.callback_action) {
            buttonProps.action.buttonInfo.callback_action = getRedirectCallbackParams({
              action: buttonProps.action,
              btn,
              props,
            });
          }
          break;
        case 'cancelOrder':
          // 提供的cancel reasons为空，则不展示cancel按钮
          if (!btn?.extra_param?.order_extra_info?.cancel_reasons?.length && !props.isHistory) {
            buttonProps.hidden = true;
          }
          break;
        case 'autoPopupDriverOrderList':
          if (props.shouldDisplayInstantly) {
            buttonProps.auto_action = buttonProps.action;
          }
          buttonProps.action.fromTFE = true;
          break;
        default:
          break;
      }

      switch (getButtonAction(btn?.multiple_action?.action_type)) {
        case 'next':
          buttonProps.multiple_action.nextParams = {
            message_text: btn.button_text,
            input_type: INPUT_TYPE.BUTTON,
            session_id: props.sessionId,
            dialogue_id: props.dialogueId,
            thread_node_id: props.threadNodeId,
            thread_id: props.threadId,
            thread_id_v2: props.threadIdV2,
            round_id: props.roundId,
            is_bypass: btn?.multiple_action?.is_bypass || false,
            message_extra: { task_bot_action: Object.assign({ task_bot_info: props.taskbotInfo }, btn.multiple_action.task_bot_action) },
          };
          break;
        case 'getIntent':
          const intent = btn?.multiple_action?.intent;
          buttonProps.multiple_action.nextParams = {
            session_id: props.sessionId,
            intent_id: intent?.intent_id,
            kb_bot_id: intent?.bot_id,
            source: IntentSource.KB,
            is_category: false,
            thread_node_id: props.threadNodeId,
            thread_id: props.threadId,
            thread_id_v2: props.threadIdV2,
            round_id: props.roundId,
            is_bypass: btn?.multiple_action?.is_bypass || false,
            dialogue_id: props.dialogueId,
          };
          break;
        // SPCB-28906 新增 webform
        case 'popupWebform':
          if (buttonProps.multiple_action.buttonInfo.callback_action) {
            buttonProps.multiple_action.buttonInfo.callback_action = getRedirectCallbackParams({
              action: buttonProps.multiple_action,
              btn,
              props,
            });
          }
          break;
        case 'redirection':
          if (buttonProps.multiple_action.buttonInfo.callback_action) {
            buttonProps.multiple_action.buttonInfo.callback_action = getRedirectCallbackParams({
              action: buttonProps.multiple_action,
              btn,
              props,
            });
          }
          break;
        case 'cancelOrder':
          // 提供的cancel reasons为空，则不展示cancel按钮
          if (!btn?.extra_param?.order_extra_info?.cancel_reasons?.length && !props.isHistory) {
            buttonProps.hidden = true;
          }
          break;
        default:
          break;
      }

      return buttonProps;
    }),
  };

  return buttonGroup;
}

export function getRedirectCallbackParams(data: { action: any; btn: any; props: any }) {
  const { action = {}, btn = {}, props = {}} = data;
  const isByPass = action?.is_bypass || false;
  const callback_action = {
    ...action.buttonInfo.callback_action,
    nextParams: {
      message_text: btn.button_text,
      input_type: INPUT_TYPE.BUTTON,
      session_id: props.sessionId,
      dialogue_id: props.dialogueId,
      thread_node_id: props.threadNodeId,
      thread_id: props.threadId,
      thread_id_v2: props.threadIdV2,
      round_id: props.roundId,
      is_bypass: isByPass,
      message_extra: {
        task_bot_action: Object.assign(
          { task_bot_info: props.taskbotInfo },
          action.buttonInfo.callback_action?.task_bot_action,
          { button_id: btn.button_id },
        ),
      },
    },
  };
  return callback_action;
}
